import { useEffect, useState, type FormEvent } from "react";
import { flushSync } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { api } from "../../api";
import { logoUrl } from "../../branding";
import { formatDateTime, formatTime } from "../../../shared/time";
import type { CalendarItem, CheckIn, EventDetail, Person } from "../../../shared/types";
import { Badge, ErrorNote } from "../../ui";
import { useSession } from "../../shell/session";
import { PageFrame, hasRole, messageOf } from "../calendar/chrome";
import { addDays, rangeFor, todayKey } from "../calendar/dates";

interface CheckInPayload {
  checkins: CheckIn[];
  inside: number;
  event: EventDetail | null;
}

interface Slip {
  heading: string;
  cards: Array<{ name: string; detail: string }>;
}

function displayName(row: CheckIn): string {
  return row.personName || row.guestName || "Guest";
}

export function CheckInPage() {
  const [params, setParams] = useSearchParams();
  const eventId = params.get("eventId") ?? "";
  const { user } = useSession();
  const canEdit = hasRole(user?.role, "coordinator");
  const [events, setEvents] = useState<CalendarItem[]>([]);
  const [roster, setRoster] = useState<CheckInPayload | null>(null);
  const [query, setQuery] = useState("");
  const [people, setPeople] = useState<Person[]>([]);
  const [guest, setGuest] = useState("");
  const [station, setStation] = useState("main");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState("");
  const [slip, setSlip] = useState<Slip | null>(null);

  useEffect(() => {
    const bounds = rangeFor(addDays(todayKey(), -30), addDays(todayKey(), 120));
    void api<CalendarItem[]>(`/api/v1/calendar?from=${encodeURIComponent(bounds.from)}&to=${encodeURIComponent(bounds.to)}`)
      .then((items) => setEvents(items.filter((item) => item.kind !== "plan").slice().sort((a, b) => a.startsAt.localeCompare(b.startsAt))))
      .catch((caught: unknown) => setError(messageOf(caught)));
  }, []);

  useEffect(() => {
    if (!eventId) {
      setRoster(null);
      return;
    }
    let live = true;
    setError("");
    void api<CheckInPayload>(`/api/v1/check-ins?eventId=${encodeURIComponent(eventId)}`)
      .then((payload) => { if (live) setRoster(payload); })
      .catch((caught: unknown) => { if (live) { setRoster(null); setError(messageOf(caught)); } });
    return () => { live = false; };
  }, [eventId]);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 1) {
      setPeople([]);
      return;
    }
    const handle = window.setTimeout(() => {
      void api<Person[]>(`/api/v1/people?q=${encodeURIComponent(term)}&limit=8`).then(setPeople).catch(() => setPeople([]));
    }, 160);
    return () => window.clearTimeout(handle);
  }, [query]);

  useEffect(() => {
    const clear = () => setSlip(null);
    window.addEventListener("afterprint", clear);
    return () => window.removeEventListener("afterprint", clear);
  }, []);

  async function reload() {
    if (!eventId) return;
    setRoster(await api<CheckInPayload>(`/api/v1/check-ins?eventId=${encodeURIComponent(eventId)}`));
  }

  async function checkInPerson(person: Person) {
    setBusy(person.id);
    setError("");
    setNotice("");
    try {
      await api(`/api/v1/check-ins`, { method: "POST", body: JSON.stringify({ eventId, personId: person.id, guestName: null, station: station.trim() || "main" }) });
      await reload();
      setNotice(`${person.name} is checked in at ${station.trim() || "main"}.`);
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setBusy("");
    }
  }

  async function checkInGuest(event: FormEvent) {
    event.preventDefault();
    const guestName = guest.trim();
    if (!guestName) {
      setError("Enter a guest name or choose a person.");
      return;
    }
    setBusy("guest");
    setError("");
    setNotice("");
    try {
      await api(`/api/v1/check-ins`, { method: "POST", body: JSON.stringify({ eventId, personId: null, guestName, station: station.trim() || "main" }) });
      await reload();
      setGuest("");
      setNotice(`${guestName} is checked in.`);
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setBusy("");
    }
  }

  async function checkOut(row: CheckIn) {
    setBusy(row.id);
    setError("");
    try {
      await api(`/api/v1/check-ins/${row.id}/checkout`, { method: "POST" });
      await reload();
      setNotice(`${displayName(row)} is checked out.`);
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setBusy("");
    }
  }

  function printSlip(next: Slip) {
    flushSync(() => setSlip(next));
    window.print();
  }

  const rows = roster?.checkins ?? [];
  const inside = rows.filter((row) => row.status === "in");
  const outside = rows.filter((row) => row.status === "out");
  const insideIds = new Set(inside.map((row) => row.personId).filter((id): id is string => Boolean(id)));
  const event = roster?.event ?? null;
  const atCapacity = event?.capacity != null && inside.length >= event.capacity;

  function cardFor(row: CheckIn) {
    return {
      name: displayName(row),
      detail: `${event?.title ?? "Event"} · ${formatTime(row.checkedInAt)} · ${row.station}`,
    };
  }

  return (
    <PageFrame
      className="print-root"
      kicker="Operations"
      title="Check-in"
      lede="Mark people in and out for one event. Badges print from this browser. No label printer is connected, and this desk does not collect children's records."
    >
      <div className="no-print stack">
        {error && <ErrorNote>{error}</ErrorNote>}
        {notice && <p className="ok-note" role="status">{notice}</p>}
        {!canEdit && <p className="notice">Check-in needs a coordinator login. You can still see the event list.</p>}
        <div className="ops-split">
          <div className="stack">
            <article className="panel panel-pad stack">
              <div className="field">
                <label htmlFor="checkin-event">Event</label>
                <select id="checkin-event" value={eventId} onChange={(event) => setParams(event.target.value ? { eventId: event.target.value } : {})}>
                  <option value="">Choose an event</option>
                  {eventId && !events.some((item) => item.id === eventId) ? <option value={eventId}>Selected event</option> : null}
                  {events.map((item) => <option key={item.id} value={item.id}>{formatDateTime(item.startsAt)} — {item.title}</option>)}
                </select>
              </div>
              {event ? (
                <p className="muted">{event.place || "Place not set"}{event.roomName ? ` · ${event.roomName}` : ""} · {event.capacity == null ? "No capacity limit" : `${inside.length} inside of ${event.capacity}`}</p>
              ) : <p className="muted">Choose an event to load the roster. Plans without an event are not check-in stations.</p>}
              {atCapacity && <p className="notice">The room is at capacity. Someone already checked out can come back in. A new person will be refused.</p>}
              <div className="field">
                <label htmlFor="station">Station</label>
                <input id="station" value={station} onChange={(event) => setStation(event.target.value)} maxLength={40} />
              </div>
              <div className="cluster">
                {["main", "door", "hall"].map((name) => (
                  <button key={name} className="btn small" type="button" aria-pressed={station === name} onClick={() => setStation(name)}>{name}</button>
                ))}
              </div>
            </article>
            <article className="panel panel-pad stack">
              <div className="field">
                <label htmlFor="person-search">Find a person</label>
                <input id="person-search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Name" />
              </div>
              {query.trim().length < 1 ? <p className="faint">Type a name to find someone. An empty search is not the whole directory.</p> : null}
              {query.trim().length > 0 && people.length === 0 ? <p className="muted">No one matches that name.</p> : null}
              {people.map((person) => (
                <button key={person.id} className="btn choice" type="button" disabled={!eventId || !canEdit || busy === person.id || insideIds.has(person.id)} onClick={() => void checkInPerson(person)}>
                  <span>{person.name}{person.householdName ? <span className="faint"> · {person.householdName}</span> : null}</span>
                  <span>{insideIds.has(person.id) ? "Inside" : busy === person.id ? "Saving…" : "Check in"}</span>
                </button>
              ))}
              <form className="form-grid" onSubmit={(event) => void checkInGuest(event)}>
                <div className="field">
                  <label htmlFor="guest-name">Guest name</label>
                  <input id="guest-name" value={guest} onChange={(event) => setGuest(event.target.value)} maxLength={140} />
                </div>
                <button className="btn primary" type="submit" disabled={!eventId || !canEdit || busy === "guest"}>{busy === "guest" ? "Saving…" : "Check in guest"}</button>
              </form>
            </article>
          </div>
          <div className="stack">
            <article className="panel panel-pad split">
              <div><strong>{inside.length}</strong><div className="faint">Inside now</div></div>
              <div><strong>{outside.length}</strong><div className="faint">Checked out</div></div>
              <div><strong>{rows.length}</strong><div className="faint">Attendance rows</div></div>
            </article>
            <Roster title="Inside" rows={inside} busy={busy} onOut={(row) => void checkOut(row)} onPrint={(row) => printSlip({ heading: "Badge", cards: [cardFor(row)] })} />
            <div className="cluster">
              <button className="btn" type="button" disabled={inside.length === 0} onClick={() => printSlip({ heading: event?.title ?? "Badges", cards: inside.map(cardFor) })}>Print badges for everyone inside</button>
              <button className="btn ghost" type="button" disabled={rows.length === 0} onClick={() => printSlip({ heading: `${event?.title ?? "Attendance"} roster`, cards: rows.map((row) => ({ name: displayName(row), detail: `${row.status} · ${formatDateTime(row.checkedInAt)} · ${row.station}` })) })}>Print attendance</button>
            </div>
              {eventId && inside.length === 0 ? <p className="faint">Nobody is inside, so badge printing stays off until someone checks in.</p> : null}
              {eventId && rows.length === 0 ? <p className="faint">Attendance print stays off until the roster has a row.</p> : null}
            <Roster title="Checked out" rows={outside} busy={busy} />
          </div>
        </div>
      </div>
      <div className="print-only">
        <p>{slip?.heading}</p>
        {slip?.cards.map((card) => (
          <article key={`${card.name}-${card.detail}`} className="badge-card">
            <p className="badge-brand"><img src={logoUrl} alt="" />MB Live</p>
            <strong>{card.name}</strong>
            <p>{card.detail}</p>
          </article>
        ))}
      </div>
    </PageFrame>
  );
}

function Roster({ title, rows, busy, onOut, onPrint }: {
  title: string;
  rows: CheckIn[];
  busy: string;
  onOut?: (row: CheckIn) => void;
  onPrint?: (row: CheckIn) => void;
}) {
  return (
    <article className="panel table-wrap">
      <div className="panel-pad"><h2>{title}</h2></div>
      {rows.length === 0 ? <p className="panel-pad muted">None.</p> : (
        <table className="data">
          <thead><tr><th>Name</th><th>In</th><th>Out</th><th>Station</th><th></th></tr></thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{displayName(row)} {row.personId ? null : <Badge>Guest</Badge>}</td>
                <td>{formatTime(row.checkedInAt)}</td>
                <td>{row.checkedOutAt ? formatTime(row.checkedOutAt) : "—"}</td>
                <td>{row.station}</td>
                <td className="cluster">
                  {onPrint ? <button className="btn small" type="button" onClick={() => onPrint(row)}>Print badge</button> : null}
                  {onOut ? <button className="btn small ghost" type="button" disabled={busy === row.id} onClick={() => onOut(row)}>{busy === row.id ? "Saving…" : "Check out"}</button> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </article>
  );
}
